import { useCallback, useEffect, useState } from 'react';
import {
  Sparkles,
  Megaphone,
  AlarmClock,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import apiConfig from '../../apiConfig';
import CustomerHeader from '../../components/CustomerHeader';
import BottomNav from '../../components/BottomNav';
import PushNotificationCard from '../../components/PushNotificationCard';
import { BRAND_TIFFANY_BLUE } from '../../constants/branding';
import SubpageHeader from '../Reserve/SubpageHeader';
import { syncAppBadge } from '../../utils/appBadge';
import { useI18n } from '../../i18n';
import type { ReserveStore as StoreContact } from '../../utils/reservation/by-Date/fetchStoresForReserve';

type CustomerMessage = {
  id: number;
  source_kind?: 'promotion' | 'followup' | 'reminder' | null;
  source_label?: string | null;
  category?: string | null;
  title: string;
  body: string;
  image_url?: string | null;
  deeplink: string | null;
  is_read: number;
  created_at: string;
};

const MESSAGE_CHANGED_EVENT = 'customer-messages:changed';
const URL_PATTERN = /https?:\/\/[^\s]+/g;
const TRAILING_URL_PUNCTUATION = /[),.!?:;]+$/;

const renderMessageBody = (body: string) => {
  const parts = body.split(URL_PATTERN);
  const urls = body.match(URL_PATTERN) ?? [];

  return parts.flatMap((part, index) => {
    const urlWithPunctuation = urls[index];
    if (!urlWithPunctuation) return [part];

    const trailingPunctuation = urlWithPunctuation.match(TRAILING_URL_PUNCTUATION)?.[0] ?? '';
    const url = trailingPunctuation
      ? urlWithPunctuation.slice(0, -trailingPunctuation.length)
      : urlWithPunctuation;

    return [
      part,
      <a
        key={`${url}-${index}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all font-medium text-sky-700 underline decoration-sky-300 underline-offset-2"
      >
        {url}
      </a>,
      trailingPunctuation,
    ];
  });
};

const TelegramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <path
      fill="currentColor"
      d="M21.4 4.6c.4-.2.8.2.7.7l-3 14.14c-.1.46-.63.69-1.03.46l-4.83-2.81-2.47 2.38c-.35.34-.95.1-.95-.39v-3.49l8.22-7.42c.28-.25-.05-.68-.37-.46l-9.96 6.28-4.38-1.37c-.5-.16-.54-.86-.06-1.08L21.4 4.6Z"
    />
  </svg>
);

const MessagesPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [markingRead, setMarkingRead] = useState<boolean>(false);
  const [messages, setMessages] = useState<CustomerMessage[]>([]);
  const [storeContacts, setStoreContacts] = useState<StoreContact[]>([]);
  const latestMessage = messages[0] ?? null;
  const unreadCount = messages.filter((message) => message.is_read === 0).length;

  const emitMessageChanged = (): void => {
    window.dispatchEvent(new Event(MESSAGE_CHANGED_EVENT));
  };

  const loadMessages = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await api.get<{ items: CustomerMessage[] }>(
        '/api/customer-app/messages',
        {
          params: { limit: 100, offset: 0 },
        },
      );
      const nextItems = [...(data.items ?? [])].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      setMessages(nextItems);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    const loadStoreContacts = async (): Promise<void> => {
      try {
        const res = await fetch(`${apiConfig.baseURL}/api/customer-app/reservation/by-date/stores`);
        if (!res.ok) return;
        const data = (await res.json()) as StoreContact[];
        const nextContacts = Array.isArray(data)
          ? data.filter((store) => Boolean(store.telegram_url))
          : [];
        setStoreContacts(nextContacts);
      } catch (error) {
        console.error(error);
      }
    };

    void loadStoreContacts();
  }, []);

  const markAllMessagesRead = useCallback(async (): Promise<void> => {
    if (unreadCount === 0 || markingRead) return;

    setMarkingRead(true);
    try {
      await api.patch('/api/customer-app/messages/mark-read/all');
      setMessages((current) => current.map((message) => ({ ...message, is_read: 1 })));
      void syncAppBadge(0);
      emitMessageChanged();
    } catch (error) {
      console.error(error);
    } finally {
      setMarkingRead(false);
    }
  }, [markingRead, unreadCount]);

  const getMessageMeta = (message: CustomerMessage) => {
    const normalized = [
      message.category,
      message.source_label,
      message.title,
      message.body,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (message.category === 'canceled' || normalized.includes('canceled') || normalized.includes('cancelled')) {
      return {
        label: t('messages.canceled'),
        icon: XCircle,
        badgeClass: 'border-rose-200 bg-rose-50 text-rose-700',
        panelClass: 'border-rose-200 bg-[linear-gradient(180deg,rgba(255,241,242,0.95),rgba(255,255,255,1))]',
        iconClass: 'bg-rose-100 text-rose-700',
      };
    }

    if (message.category === 'created' || normalized.includes('confirmed')) {
      return {
        label: t('messages.confirmed'),
        icon: CheckCircle2,
        badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        panelClass: 'border-emerald-200 bg-[linear-gradient(180deg,rgba(236,253,245,0.95),rgba(255,255,255,1))]',
        iconClass: 'bg-emerald-100 text-emerald-700',
      };
    }

    if (message.category === 'rescheduled' || normalized.includes('updated') || normalized.includes('rescheduled')) {
      return {
        label: t('messages.updated'),
        icon: RefreshCw,
        badgeClass: 'border-cyan-200 bg-cyan-50 text-cyan-700',
        panelClass: 'border-cyan-200 bg-[linear-gradient(180deg,rgba(236,254,255,0.95),rgba(255,255,255,1))]',
        iconClass: 'bg-cyan-100 text-cyan-700',
      };
    }

    if (message.source_kind === 'followup') {
      return {
        label: message.source_label || t('messages.followUp'),
        icon: Sparkles,
        badgeClass: 'border-violet-200 bg-violet-50 text-violet-700',
        panelClass: 'border-violet-200 bg-[linear-gradient(180deg,rgba(245,243,255,0.95),rgba(255,255,255,1))]',
        iconClass: 'bg-violet-100 text-violet-700',
      };
    }

    if (message.source_kind === 'reminder' || normalized.includes('reminder')) {
      return {
        label: t('messages.reminder'),
        icon: AlarmClock,
        badgeClass: 'border-amber-200 bg-amber-50 text-amber-700',
        panelClass: 'border-amber-200 bg-[linear-gradient(180deg,rgba(255,251,235,0.95),rgba(255,255,255,1))]',
        iconClass: 'bg-amber-100 text-amber-700',
      };
    }

    return {
      label: message.source_label || t('messages.promotion'),
      icon: Megaphone,
      badgeClass: 'border-sky-200 bg-sky-50 text-sky-700',
      panelClass: 'border-sky-200 bg-[linear-gradient(180deg,rgba(240,249,255,0.95),rgba(255,255,255,1))]',
      iconClass: 'bg-sky-100 text-sky-700',
    };
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-gradient-to-b from-[#f3fbf9] via-white to-[#eef7fb]"
      style={{ ['--bottom-nav-space' as string]: '108px' }}
    >
      <CustomerHeader />

      <main
        className="flex-1 px-4 pt-5"
        style={{ paddingBottom: 'calc(var(--bottom-nav-space) + env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-lg mx-auto space-y-4">
          <SubpageHeader title={t('messages.title')} onBack={() => navigate('/reserve')} />

          <section
            className="rounded-[30px] border border-[#8fdedb] px-5 py-5 text-white shadow-[0_20px_46px_rgba(10,186,181,0.2)]"
            style={{
              background: `linear-gradient(135deg, ${BRAND_TIFFANY_BLUE}, #089d99)`,
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">
                  {t('messages.inbox')}
                </div>
                <div className="mt-2 text-[28px] font-semibold tracking-[-0.03em]">
                  {messages.length}
                </div>
                <div className="text-sm text-white/72">
                  {t('messages.storeMessages')}
                </div>
                {unreadCount > 0 ? (
                  <div className="mt-3 inline-flex rounded-full bg-white/18 px-3 py-1 text-xs font-semibold text-white">
                    {t('messages.unread', { count: unreadCount })}
                  </div>
                ) : null}
              </div>

              <div className="rounded-[22px] border border-white/20 bg-white/10 px-4 py-3 text-right">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/55">
                  {t('messages.latest')}
                </div>
                <div className="mt-1 text-sm font-semibold text-white">
                  {latestMessage
                    ? new Date(latestMessage.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                    : '-'}
                </div>
              </div>
            </div>
          </section>

          <PushNotificationCard mode="notice" onOpenSettings={() => navigate('/settings')} />

          {storeContacts.length > 0 ? (
            <section className="rounded-[30px] border border-slate-200 bg-white/95 p-4 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
              <div className="mb-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {t('messages.officialAccount')}
                </div>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('messages.contactStore')}</h2>
              </div>

              <div className="space-y-3">
                {storeContacts.map((store) => (
                  <article
                    key={store.id}
                    className="rounded-[22px] border border-slate-200 bg-slate-50/85 px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900">{store.name}</div>
                        {store.location ? (
                          <div className="mt-0.5 text-xs text-slate-500">{store.location}</div>
                        ) : null}
                      </div>

                      {store.telegram_url ? (
                        <a
                          href={store.telegram_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex shrink-0 items-center gap-2 rounded-[14px] border border-sky-200 bg-sky-50 px-3 py-2 text-sky-700 transition hover:bg-sky-100"
                          aria-label={t('messages.openTelegram', { store: store.name })}
                        >
                          <div className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-sky-500 text-white">
                            <TelegramIcon className="h-5 w-5" />
                          </div>
                          <span className="text-[13px] font-semibold whitespace-nowrap">
                            {t('messages.inquiry')}
                          </span>
                        </a>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section className="rounded-[30px] border border-slate-200 bg-white/95 p-4 shadow-[0_18px_42px_rgba(15,23,42,0.08)]">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {t('messages.title')}
                </div>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('messages.storeMessages')}</h2>
              </div>
              {unreadCount > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    void markAllMessagesRead();
                  }}
                  disabled={markingRead}
                  className="min-h-[40px] rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {markingRead ? t('messages.marking') : t('messages.markAllRead')}
                </button>
              ) : (
                <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {messages.length}
                </div>
              )}
            </div>

            {loading ? (
              <p className="text-sm text-slate-500">{t('messages.loading')}</p>
            ) : messages.length === 0 ? (
              <div className="rounded-[28px] border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                {t('messages.empty')}
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((message) => (
                  (() => {
                    const meta = getMessageMeta(message);
                    const MetaIcon = meta.icon;
                    return (
                      <article
                        key={message.id}
                        className={`rounded-[28px] border p-4 shadow-sm transition ${meta.panelClass} ${message.is_read === 0 ? 'ring-2 ring-[#10bab5]/30' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex min-w-0 items-start gap-3">
                            <div className={`rounded-[20px] p-3 ${meta.iconClass}`}>
                              <MetaIcon className="h-[18px] w-[18px]" />
                            </div>
                            <div className="min-w-0 space-y-2">
                              {message.source_kind !== 'followup' ? (
                                <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold ${meta.badgeClass}`}>
                                  {meta.label}
                                </span>
                              ) : null}
                              {message.is_read === 0 ? (
                                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700">
                                  {t('messages.new')}
                                </span>
                              ) : null}
                              <h3 className="text-[15px] font-semibold leading-6 text-slate-900">
                                {message.title || t('messages.fallbackTitle')}
                              </h3>
                            </div>
                          </div>
                          <div className="rounded-full bg-white/70 px-2.5 py-1 text-[11px] text-slate-500">
                            {new Date(message.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                          {renderMessageBody(message.body)}
                        </p>
                        {message.image_url ? (
                          <img
                            src={message.image_url}
                            alt={message.title || t('messages.imageAlt')}
                            loading="lazy"
                            className="mt-3 h-auto w-full border border-slate-200 object-contain"
                          />
                        ) : null}
                        <div className="mt-3 text-[11px] font-medium text-slate-500">
                          {new Date(message.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </article>
                    );
                  })()
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default MessagesPage;
